import React, { useState, useEffect, useRef } from 'react';
import { Project, ProjectCategory } from '../types';
import { analyzeProjectText } from '../services/geminiService';
import { extractYouTubeId } from '../services/youtubeService';
import { uploadProjectPreview } from '../services/storageService';
import { Plus, Save, X, Sparkles, Loader2, Link as LinkIcon, Edit2, Tag, Youtube, Smartphone, Monitor, ImagePlus, Upload, AlertCircle } from 'lucide-react';

interface ProjectFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (project: Partial<Project>) => void;
  initialData?: Project | null;
  nextIdSuggestion?: string;
}

export const ProjectFormModal: React.FC<ProjectFormModalProps> = ({ isOpen, onClose, onSubmit, initialData, nextIdSuggestion }) => {
  const [formData, setFormData] = useState<Partial<Project>>({
    id: '',
    title: '',
    description: '',
    url: '',
    thumbnailUrl: '', // Will serve as fallback
    desktopPreviewUrl: '',
    mobilePreviewUrl: '',
    tags: [],
    category: 'Web App',
    youtubeVideoId: ''
  });
  
  const [youtubeLinkInput, setYoutubeLinkInput] = useState('');
  const [tagInput, setTagInput] = useState('');
  
  // Separate loading states
  const [loadingMagicFill, setLoadingMagicFill] = useState(false);
  const [uploadingType, setUploadingType] = useState<'desktop' | 'mobile' | null>(null);
  
  const [statusMessage, setStatusMessage] = useState<{text: string, type: 'info' | 'error' | 'success'} | null>(null);

  // Refs for file inputs
  const desktopFileRef = useRef<HTMLInputElement>(null);
  const mobileFileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      if (initialData) {
        setFormData({ ...initialData });
        setYoutubeLinkInput(initialData.youtubeVideoId ? `https://youtube.com/watch?v=${initialData.youtubeVideoId}` : '');
      } else {
        setFormData({
          id: nextIdSuggestion || '1',
          title: '',
          description: '',
          url: '',
          thumbnailUrl: `https://picsum.photos/600/400?random=${Date.now()}`,
          desktopPreviewUrl: '',
          mobilePreviewUrl: '',
          tags: [],
          category: 'Web App',
          youtubeVideoId: ''
        });
        setYoutubeLinkInput('');
      }
      setTagInput('');
      setStatusMessage(null);
    }
  }, [initialData, isOpen, nextIdSuggestion]);

  useEffect(() => {
    const id = extractYouTubeId(youtubeLinkInput);
    if (id) {
        setFormData(prev => ({ ...prev, youtubeVideoId: id }));
    } else if (!youtubeLinkInput) {
        setFormData(prev => ({ ...prev, youtubeVideoId: '' }));
    }
  }, [youtubeLinkInput]);

  // 1. Text Only Magic Fill
  const handleMagicFill = async () => {
    if (!formData.url) {
      alert("Please enter a URL first.");
      return;
    }
    
    setLoadingMagicFill(true);
    setStatusMessage({ text: 'Fetching metadata...', type: 'info' });
    
    try {
      // Fetch Metadata Only (No Screenshot)
      const microResponse = await fetch(`https://api.microlink.io?url=${encodeURIComponent(formData.url)}&meta=true`);
      const microData = await microResponse.json();
      
      let detectedTitle = '';
      let detectedDesc = '';

      if (microData.status === 'success') {
          const { title, description } = microData.data;
          detectedTitle = title || '';
          detectedDesc = description || '';
      }

      setStatusMessage({ text: 'AI analyzing...', type: 'info' });
      // Even if metadata failed, try to infer from what we have or defaults
      const aiData = await analyzeProjectText(detectedTitle || 'New Project', detectedDesc || 'Web Application');
      
      setFormData(prev => ({
        ...prev,
        title: detectedTitle || prev.title,
        description: detectedDesc || prev.description,
        tags: aiData.tags || [],
        category: (aiData.category as ProjectCategory) || 'Web App'
      }));

      setStatusMessage({ text: 'Text filled!', type: 'success' });
    } catch (e: any) {
      console.error(e);
      setStatusMessage({ text: 'Auto-fill failed.', type: 'error' });
    } finally {
      setLoadingMagicFill(false);
      setTimeout(() => setStatusMessage(null), 3000);
    }
  };

  // 2. Manual File Upload ONLY (Generator Removed)
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>, type: 'desktop' | 'mobile') => {
      const file = e.target.files?.[0];
      if (!file) return;
      
      if (!formData.id) {
          alert("Please enter Day # first (Required for folder name).");
          e.target.value = ''; // Reset
          return;
      }

      setUploadingType(type);
      setStatusMessage({ text: `Uploading ${type}...`, type: 'info' });

      try {
          const downloadUrl = await uploadProjectPreview(file, formData.id, type);
          setFormData(prev => ({
              ...prev,
              [type === 'desktop' ? 'desktopPreviewUrl' : 'mobilePreviewUrl']: downloadUrl,
              // If desktop, set as thumbnail too
              ...(type === 'desktop' ? { thumbnailUrl: downloadUrl } : {})
          }));
          setStatusMessage({ text: 'Upload complete!', type: 'success' });
          
          // Force clear message after delay
          setTimeout(() => setStatusMessage(null), 3000);
      } catch (error: any) {
          console.error("Upload error", error);
          setStatusMessage({ text: error.message, type: 'error' });
          alert(`Upload Failed: ${error.message}\n\nCheck browser console for details.`);
      } finally {
          setUploadingType(null);
          e.target.value = ''; 
      }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.title && formData.url && formData.description && formData.id) {
      onSubmit(formData);
      onClose();
    } else {
      alert("Please fill in required fields (Day #, Title, URL, Description)");
    }
  };

  const addTag = () => {
    if (tagInput.trim()) {
      setFormData(prev => ({
        ...prev,
        tags: [...(prev.tags || []), tagInput.trim()]
      }));
      setTagInput('');
    }
  };

  const removeTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags?.filter(t => t !== tagToRemove)
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-fade-in">
      <div className="bg-surface border border-white/10 p-6 rounded-2xl w-full max-w-lg shadow-2xl relative flex flex-col max-h-[90vh]">
        <button onClick={onClose} className="absolute top-4 right-4 text-slate-400 hover:text-white transition-colors">
          <X size={20} />
        </button>

        <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
          {initialData ? <Edit2 size={20} className="text-primary" /> : <Plus size={20} className="text-primary" />}
          {initialData ? 'Edit Project' : 'New Project'}
        </h2>

        <form onSubmit={handleSubmit} className="space-y-5 overflow-y-auto pr-2 scrollbar-thin">
          
          {/* Top Row: Day & Category */}
          <div className="grid grid-cols-3 gap-4">
             <div className="col-span-1">
                <label className="block text-xs font-semibold text-slate-400 mb-1.5 ml-1">Day #</label>
                <div className="relative">
                   <Plus size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                   <input 
                      className="w-full bg-black/30 border border-white/10 rounded-lg py-2.5 pl-9 pr-3 text-white focus:border-primary outline-none text-sm transition-all font-mono text-center" 
                      placeholder="1"
                      value={formData.id}
                      onChange={e => setFormData({...formData, id: e.target.value})}
                      required
                   />
                </div>
             </div>
             <div className="col-span-2">
                <label className="block text-xs font-semibold text-slate-400 mb-1.5 ml-1">Category</label>
                <select 
                    className="w-full bg-black/30 border border-white/10 rounded-lg p-2.5 text-white outline-none text-sm focus:border-primary transition-all appearance-none"
                    value={formData.category}
                    onChange={e => setFormData({...formData, category: e.target.value as ProjectCategory})}
                >
                    <option value="Web App">Web App</option>
                    <option value="Frontend">Frontend</option>
                    <option value="Full-Stack">Full-Stack</option>
                    <option value="3D/Creative">3D/Creative</option>
                    <option value="Utility">Utility</option>
                </select>
             </div>
          </div>

          {/* URL & Magic Fill (Text Only) */}
          <div>
            <label className="block text-xs font-semibold text-slate-400 mb-1.5 ml-1">Project URL</label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                 <LinkIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                 <input 
                  className="w-full bg-black/30 border border-white/10 rounded-lg py-2.5 pl-9 pr-3 text-white focus:border-primary outline-none text-sm transition-all" 
                  placeholder="https://my-app.pages.dev"
                  value={formData.url}
                  onChange={e => setFormData({...formData, url: e.target.value})}
                  required
                />
              </div>
              <button
                type="button"
                onClick={handleMagicFill}
                disabled={loadingMagicFill || !formData.url}
                className="bg-accent/10 text-accent hover:bg-accent/20 disabled:opacity-50 disabled:cursor-not-allowed border border-accent/20 rounded-lg px-3 transition-colors flex items-center gap-2 font-medium text-xs whitespace-nowrap"
                title="Fill Title, Desc & Tags (Text Only)"
              >
                {loadingMagicFill ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                Magic Fill
              </button>
            </div>
          </div>

          <div>
             <label className="block text-xs font-semibold text-slate-400 mb-1.5 ml-1">Title</label>
             <input 
               className="w-full bg-black/30 border border-white/10 rounded-lg p-2.5 text-white focus:border-primary outline-none font-medium text-sm transition-all" 
               placeholder="e.g. Neon Dashboard"
               value={formData.title}
               onChange={e => setFormData({...formData, title: e.target.value})}
               required
             />
          </div>

          <div>
             <label className="block text-xs font-semibold text-slate-400 mb-1.5 ml-1">Description</label>
             <textarea 
               className="w-full bg-black/30 border border-white/10 rounded-lg p-2.5 text-white focus:border-primary outline-none text-sm resize-none transition-all" 
               placeholder="Briefly describe what this project does..."
               rows={3}
               value={formData.description}
               onChange={e => setFormData({...formData, description: e.target.value})}
               required
             />
          </div>

          {/* PREVIEW IMAGES SECTION (Manual Upload Only) */}
          <div className="bg-black/20 rounded-xl p-4 border border-white/5 space-y-4">
              <div className="flex items-center justify-between">
                  <label className="text-xs font-bold text-slate-300 uppercase tracking-wider flex items-center gap-2">
                      <ImagePlus size={14} className="text-primary" />
                      Manual Uploads
                  </label>
                  {/* Generator Button Removed */}
              </div>

              {/* Hidden Inputs */}
              <input 
                  type="file" 
                  ref={desktopFileRef} 
                  className="hidden" 
                  accept="image/*"
                  onChange={(e) => handleFileUpload(e, 'desktop')}
              />
              <input 
                  type="file" 
                  ref={mobileFileRef} 
                  className="hidden" 
                  accept="image/*"
                  onChange={(e) => handleFileUpload(e, 'mobile')}
              />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  
                  {/* DESKTOP */}
                  <div className="space-y-2">
                      <div className="flex justify-between items-end">
                          <span className="text-[10px] font-medium text-slate-500 flex items-center gap-1">
                              <Monitor size={12} /> Desktop (Landscape)
                          </span>
                      </div>
                      <div 
                          onClick={() => desktopFileRef.current?.click()}
                          className={`
                              group relative w-full aspect-video rounded-lg border-2 border-dashed transition-all cursor-pointer overflow-hidden
                              ${formData.desktopPreviewUrl 
                                  ? 'border-primary/50 bg-black/50' 
                                  : 'border-white/10 bg-white/5 hover:border-white/30 hover:bg-white/10'
                              }
                          `}
                      >
                          {formData.desktopPreviewUrl ? (
                              <>
                                  <img src={formData.desktopPreviewUrl} className="w-full h-full object-cover" alt="Desktop Preview" />
                                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center text-white backdrop-blur-sm">
                                      <Upload size={24} className="mb-2" />
                                      <span className="text-xs font-bold">Replace Image</span>
                                  </div>
                              </>
                          ) : (
                              <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-500 group-hover:text-slate-300 transition-colors">
                                  {uploadingType === 'desktop' ? (
                                      <Loader2 size={24} className="animate-spin text-primary" />
                                  ) : (
                                      <>
                                          <Upload size={24} className="mb-2 opacity-50 group-hover:scale-110 transition-transform" />
                                          <span className="text-xs font-medium">Click to Upload</span>
                                      </>
                                  )}
                              </div>
                          )}
                      </div>
                      <input 
                          value={formData.desktopPreviewUrl}
                          onChange={e => setFormData({...formData, desktopPreviewUrl: e.target.value})}
                          className="w-full bg-transparent border-b border-white/10 text-[10px] py-1 text-slate-400 focus:text-white focus:border-primary outline-none transition-colors"
                          placeholder="...or paste image URL"
                      />
                  </div>

                  {/* MOBILE */}
                  <div className="space-y-2">
                      <div className="flex justify-between items-end">
                          <span className="text-[10px] font-medium text-slate-500 flex items-center gap-1">
                              <Smartphone size={12} /> Mobile (Portrait)
                          </span>
                      </div>
                      <div className="flex gap-4">
                          <div 
                              onClick={() => mobileFileRef.current?.click()}
                              className={`
                                  group relative w-[100px] aspect-[9/16] rounded-lg border-2 border-dashed transition-all cursor-pointer overflow-hidden shrink-0
                                  ${formData.mobilePreviewUrl 
                                      ? 'border-primary/50 bg-black/50' 
                                      : 'border-white/10 bg-white/5 hover:border-white/30 hover:bg-white/10'
                                  }
                              `}
                          >
                              {formData.mobilePreviewUrl ? (
                                  <>
                                      <img src={formData.mobilePreviewUrl} className="w-full h-full object-cover" alt="Mobile Preview" />
                                      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center text-white backdrop-blur-sm">
                                          <Upload size={20} className="mb-1" />
                                          <span className="text-[9px] font-bold">Replace</span>
                                      </div>
                                  </>
                              ) : (
                                  <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-500 group-hover:text-slate-300 transition-colors">
                                      {uploadingType === 'mobile' ? (
                                          <Loader2 size={20} className="animate-spin text-primary" />
                                      ) : (
                                          <>
                                              <Upload size={20} className="mb-2 opacity-50 group-hover:scale-110 transition-transform" />
                                              <span className="text-[9px] font-medium text-center">Upload</span>
                                          </>
                                      )}
                                  </div>
                              )}
                          </div>
                          
                          <div className="flex-1 flex flex-col justify-center gap-2">
                               <p className="text-[10px] text-slate-500 leading-snug">
                                   Please upload a vertical screenshot <strong>(9:16 aspect ratio)</strong>.
                               </p>
                               <input 
                                  value={formData.mobilePreviewUrl}
                                  onChange={e => setFormData({...formData, mobilePreviewUrl: e.target.value})}
                                  className="w-full bg-transparent border-b border-white/10 text-[10px] py-1 text-slate-400 focus:text-white focus:border-primary outline-none transition-colors"
                                  placeholder="...or paste image URL"
                              />
                          </div>
                      </div>
                  </div>

              </div>
          </div>

          {/* YouTube & Tags */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
             <div>
                <label className="block text-xs font-semibold text-slate-400 mb-1.5 ml-1 flex items-center gap-2">
                    <Youtube size={12} /> YouTube Link
                </label>
                <input 
                  className="w-full bg-black/30 border border-white/10 rounded-lg py-2 px-3 text-white focus:border-primary outline-none text-sm" 
                  placeholder="Optional Video URL"
                  value={youtubeLinkInput}
                  onChange={e => setYoutubeLinkInput(e.target.value)}
                />
             </div>
             <div>
                <label className="block text-xs font-semibold text-slate-400 mb-1.5 ml-1 flex items-center gap-2">
                    <Tag size={12} /> Add Tags
                </label>
                <div className="relative">
                    <input 
                      className="w-full bg-black/30 border border-white/10 rounded-lg py-2 px-3 text-white focus:border-primary outline-none text-sm" 
                      placeholder="Press Enter"
                      value={tagInput}
                      onChange={e => setTagInput(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addTag())}
                    />
                    <button type="button" onClick={addTag} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"><Plus size={14}/></button>
                </div>
             </div>
          </div>

          <div className="flex flex-wrap gap-2">
              {formData.tags?.map((t, i) => (
                <span key={i} className="text-[10px] bg-primary/10 text-primary px-2 py-0.5 rounded border border-primary/20 flex items-center gap-1">
                  #{t}
                  <button type="button" onClick={() => removeTag(t)} className="hover:text-white"><X size={10} /></button>
                </span>
              ))}
          </div>

          {/* Status Bar */}
          {statusMessage && (
               <div className={`text-center text-xs font-bold py-2 rounded flex items-center justify-center gap-2 ${
                  statusMessage.type === 'error' ? 'bg-red-500/10 text-red-500' : 
                  statusMessage.type === 'success' ? 'bg-green-500/10 text-green-500' :
                  'bg-primary/10 text-primary animate-pulse'
               }`}>
                   {statusMessage.type === 'error' && <AlertCircle size={14} />}
                   {statusMessage.text}
               </div>
          )}

          <div className="pt-4 border-t border-white/5 flex gap-3">
             <button type="button" onClick={onClose} className="flex-1 py-2.5 rounded-lg border border-white/10 text-slate-400 hover:bg-white/5 hover:text-white transition-all text-sm font-medium">
                Cancel
             </button>
             <button type="submit" className="flex-1 bg-gradient-to-r from-primary to-blue-600 text-white py-2.5 rounded-lg hover:shadow-lg hover:shadow-primary/20 transition-all flex items-center justify-center gap-2 text-sm font-bold">
               <Save size={16} />
               {initialData ? 'Update Project' : 'Publish'}
             </button>
          </div>
        </form>
      </div>
    </div>
  );
};